import type {
    InterspersedQuizT,
    Recommendation,
    Score,
    UserAttemptData,
} from "#content-types-package/practice-types.ts";

/**
 * Get the source ID given the current set of props.
 *
 * For quizzes (tasks of type 'topic_quiz'), the source ID will be the position
 * key. For unit tests (tasks of type 'topic_unit_test'), the position key will
 * be null, and the source ID will be the ID of the topic itself.
 */
const getSourceId = (quiz: InterspersedQuizT): string => {
    return quiz.positionKey || quiz.topic.id;
};

type State = {
    quizScores: Record<string, Score | null | undefined>;
    recommendations: Array<Recommendation>;
    taskInProgressMap: Record<string, boolean>;
};

/**
 * Augment an InterspersedQuizT using state drawn from the UserInfoStore.
 */
const augmentWithUserInfoState = (
    quiz: InterspersedQuizT,
    {quizScores, recommendations, taskInProgressMap}: State,
): InterspersedQuizT => {
    // In the context of a quiz or test, we only consider an item to be
    // 'recommended' if it was recommended by the quiz or test directly.
    const recsByItem: Record<string, boolean> = {};
    recommendations
        .filter(({sourceId}) => sourceId === getSourceId(quiz))
        .forEach(({kind, contentId}) => {
            recsByItem[`${kind}:${contentId}`] = true;
        });

    return {
        ...quiz,
        // TODO (josh): Having this special type here is making Flow
        // very unhappy. Change this to use a conventional Tutorial
        // type.
        tutorials: quiz.tutorials.map(
            ({id, contentItems, slug, title, url}) => ({
                id,
                contentItems: contentItems.map(
                    ({
                        contentId,
                        kind,
                        nodeUrl,
                        mastery,
                        title,
                        translatedTitle,
                    }) => ({
                        contentId,
                        kind,
                        nodeUrl,
                        mastery,
                        title: translatedTitle || title,
                        recommended: recsByItem[`${kind}:${contentId}`],
                    }),
                ),
                slug,
                title,
                url,
            }),
        ),
        score: quizScores[quiz.slug] || quiz.score,
    };
};

/**
 * Utility function to update a quiz or unit test with user attempt data.
 *
 * The data in question likely comes from GraphQL.
 */
const augmentWithUserAttemptData = (
    quiz: InterspersedQuizT,
    attempt?: UserAttemptData,
): InterspersedQuizT => {
    if (!attempt) {
        return quiz;
    }
    const {canResume, completedDate, numAttempted, numCorrect} = attempt;

    // @ts-expect-error: MasteryChallengeT is a union of two types, one with
    // `canResume: true` and the other with `canResume: false`.  TypeScript
    // raises an error here because it can't be sure that we aren't assigning
    // a boolean (which can be either true or false) to either of those types.
    return {
        ...quiz,
        score: attempt.isCompleted
            ? {completedDate, numAttempted, numCorrect}
            : undefined,
        canResume: canResume as boolean,
    };
};

export {getSourceId, augmentWithUserInfoState, augmentWithUserAttemptData};
